import { DEFAULT_TEMPLATE } from './default-template';
import { BOW_SEASON_TEMPLATE } from './bow-season-template';
import { FOOD_PLOT_TEMPLATE } from './food-plot-template';
import { LEASE_HUNTER_TEMPLATE } from './lease-hunter-template';
import type { ChecklistTemplate } from '@/types/checklist-template';
import type { StorageAdapter } from './storage/storage-adapter';

const BUILT_IN_TEMPLATES: ChecklistTemplate[] = [
  DEFAULT_TEMPLATE,
  BOW_SEASON_TEMPLATE,
  FOOD_PLOT_TEMPLATE,
  LEASE_HUNTER_TEMPLATE,
];

// Module-level lock — prevents concurrent calls from racing (e.g. multiple auth events firing simultaneously)
let seedingInProgress: Promise<void> | null = null;

/**
 * Seeds built-in templates on first run or when the code version is newer.
 * Idempotent — safe to call on every app startup.
 * Deduplication is by name + isBuiltIn (the adapter generates its own IDs).
 *
 * Concurrency-safe: concurrent calls share the same in-flight promise so only
 * one execution runs at a time, preventing the race where both calls see an
 * empty DB and create duplicates with overwritten task references.
 */
export function seedBuiltInTemplates(adapter: StorageAdapter): Promise<void> {
  if (seedingInProgress) return seedingInProgress;
  seedingInProgress = runSeed(adapter).finally(() => {
    seedingInProgress = null;
  });
  return seedingInProgress;
}

async function runSeed(adapter: StorageAdapter): Promise<void> {
  const allTemplates = await adapter.getTemplates();

  for (const template of BUILT_IN_TEMPLATES) {
    const { id: _id, createdAt: _c, updatedAt: _u, ...templateData } = template;

    // Find all copies with this name (duplicates created by race conditions)
    const copies = allTemplates.filter((t) => t.isBuiltIn && t.name === template.name);

    if (copies.length === 0) {
      await adapter.createTemplate(templateData);
      continue;
    }

    // Keep the copy with the most tasks; delete extras
    const [best, ...extras] = [...copies].sort((a, b) => b.tasks.length - a.tasks.length);
    for (const dupe of extras) {
      await adapter.deleteTemplate(dupe.id);
    }

    // Update if version is newer OR if the surviving copy has 0 tasks (corrupted by race)
    if (best.version < template.version || best.tasks.length === 0) {
      await adapter.updateTemplate(best.id, templateData);
    }
  }
}
